import React, { useEffect, useState } from "react";
import { Truck, Loader2 } from "lucide-react";
import { quoteShippingApi } from "../api";

/**
 * Inline pincode delivery estimator on the PDP.
 * Debounced + cancellable so rapid typing never races.
 * Reuses /api/shipping/quote — same source of truth as checkout.
 */
const PincodeEstimator = ({ price }) => {
  const [pin, setPin] = useState("");
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const p = pin.trim();
    if (!/^[0-9]{6}$/.test(p)) {
      setQuote(null);
      setErr("");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr("");
    const t = setTimeout(async () => {
      try {
        const q = await quoteShippingApi({ pincode: p, subtotal: price || 0 });
        if (!cancelled) setQuote(q);
      } catch (e) {
        if (!cancelled) {
          setErr("Couldn't fetch a live estimate. Try again shortly.");
          setQuote(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [pin, price]);

  return (
    <div className="mt-8 p-5 border border-[var(--sand)] bg-[var(--cream)]" data-testid="pincode-estimator">
      <div className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[var(--copper)] mb-3">
        <Truck className="w-4 h-4" /> Delivery estimate
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))}
          placeholder="Enter pincode"
          aria-label="Delivery pincode"
          data-testid="pincode-input"
          className="flex-1 h-11 px-4 text-sm border border-[var(--sand)] focus:outline-none focus:border-[var(--copper)] bg-white"
        />
        {loading && (
          <div className="flex items-center px-3"><Loader2 className="w-4 h-4 animate-spin text-[var(--copper)]" /></div>
        )}
      </div>

      {quote && (
        <div className="mt-4 text-sm leading-relaxed text-[var(--espresso)]/80" data-testid="pincode-result">
          {quote.serviceable !== false ? (
            <>
              <p>
                <span className="font-medium">
                  {quote.shipping === 0 ? "Free shipping" : `₹${Math.round(quote.shipping)} shipping`}
                </span>
                {quote.courier_name && <> · via {quote.courier_name}</>}
              </p>
              {quote.etd && (
                <p className="text-[var(--espresso)]/60 mt-1">
                  Delivery estimate: <span className="text-[var(--espresso)]">{quote.etd}</span>
                </p>
              )}
            </>
          ) : (
            <p className="text-[var(--espresso)]/70">
              {quote.message || "Sorry, we're not serviceable to this pincode yet. Please email us for a custom quote."}
            </p>
          )}
        </div>
      )}
      {err && !loading && <p className="mt-3 text-sm text-red-700">{err}</p>}
    </div>
  );
};

export default PincodeEstimator;
